import { useContext, useState, useEffect, useCallback } from 'react';

import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { CurrentDataContext } from 'src/app/current-data';
import { fetchWorkspacePlan } from 'src/sections/repository/actions/fetch-repository-details';
import { getCurrentUser } from 'src/selectors/user-selectors';
import {
  getWorkspacePlan,
  getWorkspacePlanRequestStatus,
} from 'src/selectors/workspace-selectors';
import urls from 'src/urls/jira';
import authRequest from 'src/utils/fetch';

import messages from '../i18n/rovo-dev-agents-page.i18n';

type JiraSite = {
  type: 'jira-site';
  cloudId: string;
  cloudName: string;
  cloudUrl: string;
  connected: boolean;
  isRovoEnabled: boolean;
  isRovoDevAgentsEnabled: boolean;
};
type SitesResponse = {
  size: number;
  page: number;
  pageLen: number;
  values: JiraSite[];
};
type RovoDevAgentsRequirements = {
  loading: boolean;
  error: FormattedMessage.MessageDescriptor | null;
  isWorkspacePaid: boolean;
  hasConnectedJiraSite: boolean;
  hasEnabledRovoDevAgents: boolean;
};
export const useRovoDevAgentsRequirements = ({
  pageLen = 100,
}: {
  pageLen?: number;
} = {}): RovoDevAgentsRequirements => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<FormattedMessage.MessageDescriptor | null>(
    null
  );
  const [hasConnectedJiraSite, setHasConnectedJiraSite] =
    useState<boolean>(false);
  const [hasEnabledRovoDevAgents, setHasEnabledRovoDevAgents] =
    useState<boolean>(false);

  const { workspace } = useContext(CurrentDataContext);

  const dispatch = useDispatch();
  const { isFetchWorkspacePlanLoading, isFetchWorkspacePlanError } =
    useSelector(getWorkspacePlanRequestStatus);
  const { isWorkspacePaid, isWorkspacePremium } = useSelector(getWorkspacePlan);
  const workspaceSlug = workspace?.slug;

  useEffect(() => {
    if (workspaceSlug && isFetchWorkspacePlanLoading) {
      dispatch(fetchWorkspacePlan(workspaceSlug));
    }
  }, [dispatch, isFetchWorkspacePlanLoading, workspaceSlug]);

  if (isFetchWorkspacePlanError && !error) {
    setError(messages.permissionsError);
  }

  const currentUser = useSelector(getCurrentUser);

  // This method will check both the connection of jira sites, and the enablement of Rovo Dev Agents.
  const assessJiraSiteConnections = useCallback(async () => {
    if (workspace && workspace.uuid && currentUser && currentUser.account_id) {
      try {
        const sites: JiraSite[] = [];

        // Recursive function for fetching pages of data until auth is found
        const getSites = async (page: number) => {
          const res = await fetch(
            authRequest(
              urls.api.internal.sitesWithRovoDevAgents(
                workspace?.uuid as string,
                currentUser?.account_id as string,
                page,
                pageLen // pageLen
              )
            )
          );
          if (!res.ok) {
            throw new Error(
              `http error status ${res.status} with text: ${res.statusText}`
            );
          }

          const sitesResponse: SitesResponse =
            (await res.json()) as SitesResponse;
          sites.push(...sitesResponse.values);

          const responseHasConnectedJira = sites.some(s => s.connected);
          const responseHasRovoDevAgents = sites.some(
            s => s.isRovoDevAgentsEnabled
          );

          // if both is true we don't need to fetch any more data
          if (responseHasConnectedJira && responseHasRovoDevAgents) {
            setHasConnectedJiraSite(responseHasConnectedJira);
            setHasEnabledRovoDevAgents(responseHasRovoDevAgents);

            return;
          }

          // if we have more data to fetch, we get the next page
          if (sites.length < sitesResponse.size) {
            await getSites(page + 1);
            return;
          }

          // No match is found and list is exhausted
          setHasConnectedJiraSite(responseHasConnectedJira);
          setHasEnabledRovoDevAgents(responseHasRovoDevAgents);
        };

        await getSites(1);
      } catch {
        setError(messages.permissionsError);
      }

      setLoading(false);
      return;
    }

    setLoading(false);
    setError(messages.permissionsError);
  }, [currentUser, pageLen, workspace]);

  useEffect(() => {
    if (!error) {
      assessJiraSiteConnections();
    }
  }, [assessJiraSiteConnections, error]);

  return {
    loading,
    error,
    isWorkspacePaid: isWorkspacePaid || isWorkspacePremium,
    hasConnectedJiraSite,
    hasEnabledRovoDevAgents,
  };
};
